import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { WorkspaceModeProvider, useWorkspaceMode } from "@/lib/workspaceMode";
import {
  LayoutDashboard,
  Users,
  Upload,
  Settings,
  UserCog,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  FileSearch,
  ListChecks,
  Briefcase,
  ClipboardList,
  GitCompare,
  ShieldAlert,
  CloudDownload,
  UserPlus,
  BadgeCheck,
  CheckCircle2,
  FolderOpen,
  Database,
  KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { VF_UI_BUILD_ID } from "@/lib/uiBuildInfo";
import {
  clearDistributorReview,
  getDistributorReviewBuyerId,
  getDistributorReviewBuyerLabel,
  getDistributorReviewOwnerId,
  setDistributorReview,
} from "@/lib/distributorReview";
import { setStoredClientId } from "@/lib/clientSelection";
import ClientRepositoryRelocateDialog from "@/components/ClientRepositoryRelocateDialog";
import ModuleClientSelector from "@/components/ModuleClientSelector";
import ModuleSidebarQuickToggle from "@/components/ModuleSidebarQuickToggle";
import {
  resolveBuyerOperationalSidebar,
  resolvePanelHubSidebarWorkModule,
} from "@/lib/buyerOperationalSidebar";
import { useBuyerModuleVisibility } from "@/lib/useBuyerModuleVisibility";
import { ClientModuleScopeProvider, resolveClientScope, isHubOrAdminPath } from "@/lib/clientModuleScope";
import { isEquipoBasePath } from "@/modules/equipo/equipoRoutes";
import { useDesignerUiAccess } from "@/lib/designerUiAccess";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import {
  hasPlatformCenterAccess,
  isBuyerLicenseConfigDeepLink,
} from "@/lib/postLoginRedirect";
import { hasMultipleAccessContexts } from "@/lib/workspaceAccessContext";
import { PLATFORM_DESIGNER_BUYER_PREVIEW_BANNER } from "@/lib/platformDesignerBuyerPreview";
import { usePlatformDesignerBuyerPreviewActions } from "@/lib/usePlatformDesignerBuyerPreview";
import { WORKSPACE_MODE_PLATFORM } from "@/lib/workspaceMode";
import { useEntitlements } from "@/modules/useEntitlements";
import { useUiPreferences } from "@/lib/useUiPreferences";
import AppBackgroundLayer from "@/components/AppBackgroundLayer";
import { filterNavByEntitlements } from "@/modules/entitlementsFromSystemInfo";
import { filterNavWithoutDespachoHonorarios } from "@/lib/teamRolePermissions";
import { WorkModuleProvider } from "@/lib/workModuleContext";
import {
  WORK_MODULES,
  defaultRouteForModule,
  getStoredWorkModule,
  isPathAllowedForModule,
  navForWorkModule,
  setStoredWorkModule,
} from "@/modules/main_ui";
import { BNTLogo } from "@/pages/layoutChrome";
import { MODULE_NAV_ICONS } from "@/modules/moduleNavIcons";
import {
  getModuleInicioPath,
  VISOR_MODULE_BROWSER_TITLE,
  VISOR_MODULE_DISPLAY_NAME,
  CONCILIACION_MANUAL_CFDI_TITLE,
} from "@/modules/workModuleCore";
import { requestLaboralFlujoCollapseToHub } from "@/lib/laboralFlujoNav";
import { TRAMITES_LEGACY_PATHS, TRAMITES_MODULE_PATHS, isTramitesTabNavActive } from "@/modules/tramites/tramitesService";
import { DESPACHO_MODULE_PATHS } from "@/modules/despacho/despachoService";
import { MATERIALIDAD_MODULE_PATHS } from "@/modules/materialidad/manifest";
import { isAnalisisFinancieroPath } from "@/modules/analisis_financiero/manifest";
import { LABORAL_LEGACY_REDIRECT_PATHS } from "@/modules/laboralNavPolicy";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import PanelHubHeaderMeta from "@/components/PanelHubHeaderMeta";
import PanelHubTopbar from "@/components/PanelHubTopbar";
import PanelLicenseActivationBlock from "@/components/PanelLicenseActivationBlock";
import { SatDownloadJobsProvider } from "@/lib/satDownloadJobs";
import {
  CONFIGURACION_DESPACHO_PATH,
  CONFIGURACION_EMISOR_PATH,
  CONFIGURACION_ALIAS_PATH,
} from "@/modules/config/configuracionRoutes";
import SatDownloadJobsIndicator from "@/components/sat/SatDownloadJobsIndicator";
import SatDownloadCompletionBanner from "@/components/sat/SatDownloadCompletionBanner";
import CertificateExpirationBanner from "@/components/CertificateExpirationBanner";
import PlatformLicenseRequestsPendingBanner from "@/components/platform/PlatformLicenseRequestsPendingBanner";
import RouteModuleEntitlementBlocked from "@/components/RouteModuleEntitlementBlocked";
import BntAsistenteShell from "@/components/asistente/BntAsistenteShell";
import { isBntAsistentePath } from "@/modules/asistente/bntAsistenteRoutes";
import { requireModuleEntitlementForRoute } from "@/modules/routeEntitlementGuard";
import { useWorkspacePresenceHeartbeat } from "@/lib/useWorkspacePresence";
import {
  captureDatosMaestrosReturnContext,
  clearDatosMaestrosReturnContext,
  isDatosMaestrosHubCardPath,
  isDatosMaestrosNavigationActive,
  parseDatosMaestrosReturnFromSearch,
  readDatosMaestrosReturnFromSession,
  resolveDatosMaestrosReturnTarget,
  shouldBypassWorkModulePathGuard,
  resolveWorkModuleForDatosMaestrosCard,
  DATOS_MAESTROS_NAV_CONTEXT,
} from "@/modules/datosMaestros/datosMaestrosNavigation";

export { BNTLogo, PageHeader } from "@/pages/layoutChrome";

const DISTRIBUIDOR_BASE_PATHS = [
  "/",
  CONFIGURACION_ALIAS_PATH,
  CONFIGURACION_DESPACHO_PATH,
  CONFIGURACION_EMISOR_PATH,
  "/cartera-licencias",
  "/catalogo-compradores",
  "/usuarios-distribuidor",
  "/ayuda",
];

/** Rutas operativas visibles al revisar un comprador con espacio en este servidor. */
const DISTRIBUIDOR_REVIEW_PATHS = [
  "/clientes",
  "/carga-informacion",
  "/descarga-automatica-sat",
  "/inscripcion-trabajadores-sat",
  "/conciliacion-manual-cfdi",
  "/visor",
  "/visores-sat",
  "/visores-sat-xml",
  "/confronta-cfdi",
  "/auditoria-efos",
  "/auditoria-estatus-local",
  "/tramites-fiscales",
  "/visor/inicio",
  "/laboral/inicio",
  "/confronta",
  "/confronta-egresos",
  "/confronta-nominas",
];

/**
 * Resuelve las pestañas del sidebar según modo de trabajo y módulo activo.
 * En el Panel General (/) la navegación lateral solo aparece si la preferencia está activa.
 */
function resolveSidebarNav({
  canSwitch,
  mode,
  reviewActive,
  user,
  workModule,
  useModuleNav,
  pathname,
  isPlatformDesigner,
  buyerModuleSidebarEnabled,
}) {
  const path = (pathname || "").split("?")[0];
  if (path === "/" || path === "") {
    const showPanelModuleNav = buyerModuleSidebarEnabled && useModuleNav;
    if (showPanelModuleNav) {
      return navForWorkModule(resolvePanelHubSidebarWorkModule(workModule));
    }
    return [];
  }
  if (isPlatformDesigner && (path === "/platform" || path.startsWith("/platform/"))) {
    return [
      PANEL_GENERAL_NAV_ITEM,
      { to: "/platform", label: "Centro de Licencias BNT", icon: KeyRound, end: false },
    ];
  }
  if (isPlatformDesigner || mode === WORKSPACE_MODE_PLATFORM) {
    return withPanelGeneralNav(
      navForWorkModule(workModule || WORK_MODULES.VISOR_SAT),
      { pathname, useModuleNav: true },
    );
  }
  if (canSwitch && mode === "distribuidor" && reviewActive) {
    return DISTRIBUIDOR_REVIEW_NAV;
  }
  if (useModuleNav) {
    return withPanelGeneralNav(
      navForWorkModule(workModule || WORK_MODULES.VISOR_SAT),
      { pathname, useModuleNav: true },
    );
  }
  if (canSwitch && mode === "distribuidor") {
    return DISTRIBUIDOR_NAV_ALL.filter(
      (item) => !item.requiresLicenseGeneration || user?.can_generate_licenses,
    );
  }
  return navForWorkModule(WORK_MODULES.VISOR_SAT);
}

function isDistribuidorAllowedPath(pathname, reviewActive, workModule, useModuleNav) {
  if (pathname === "/registro-inicial" || pathname === "/recuperacion-acceso") return true;
  if (useModuleNav) {
    return isPathAllowedForModule(pathname, workModule);
  }
  if (DISTRIBUIDOR_BASE_PATHS.includes(pathname)) return true;
  if (!reviewActive) return false;
  return DISTRIBUIDOR_REVIEW_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

const DISTRIBUIDOR_NAV_ALL = [
  { to: "/", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/catalogo-compradores", label: "Compradores y prospectos", icon: ClipboardList },
  { to: "/cartera-licencias", label: "Cartera de licencias", icon: Briefcase },
  {
    to: "/usuarios-distribuidor",
    label: "Usuarios Administradores BNT",
    icon: UserCog,
    requiresLicenseGeneration: true,
  },
  { to: CONFIGURACION_EMISOR_PATH, label: "Gestor de licencias", icon: Settings },
];

const DISTRIBUIDOR_REVIEW_NAV = [
  { to: "/clientes", label: "Empresas del comprador", icon: Users },
  { to: "/carga-informacion", label: "Carga de información", icon: Upload },
  { to: "/descarga-automatica-sat", label: "Descarga de CFDI SAT", icon: CloudDownload },
  { to: "/visor", label: "Visor de XML SAT", icon: FileSearch, end: true, testid: "nav-visor-de-xml-sat" },
  { to: "/confronta-cfdi", label: "Confronta CFDI SAT", icon: GitCompare },
  { to: "/inscripcion-trabajadores-sat", label: "Inscripción a Trabajadores SAT", icon: UserPlus },
  { to: "/auditoria-efos", label: "Auditoría CFDI (EFOS)", icon: ShieldAlert },
  { to: "/auditoria-estatus-local", label: "Auditoría estatus local", icon: ShieldAlert },
  { to: "/carga-informacion?open=conciliacion-manual", label: CONCILIACION_MANUAL_CFDI_TITLE, icon: ListChecks },
];

/** Mapa iconKey → componente Lucide (pestañas modulares). Re-exportado desde moduleNavIcons. */
const MODULE_NAV_ICON_MAP = MODULE_NAV_ICONS;

/** Mini-sidebar: ancho fijo (64px) para que los iconos nunca se compriman. */
const SIDEBAR_MINI_WIDTH_CLASS = "w-16 min-w-16 max-w-16 shrink-0";

/** Enlace al hub principal (/) visible dentro de cualquier módulo operativo. */
const PANEL_GENERAL_NAV_ITEM = {
  to: "/",
  label: "Panel General",
  iconKey: "panel",
  end: true,
  testid: "nav-panel-general",
};

function withPanelGeneralNav(navItems, { useModuleNav, pathname }) {
  const path = String(pathname || "").split("?")[0];
  if (!useModuleNav || path === "/" || path === "") return navItems;
  if (navItems.some((item) => item.to === "/")) return navItems;
  return [PANEL_GENERAL_NAV_ITEM, ...navItems];
}

export default function Layout() {
  const { user, logout, refreshUser } = useAuth();
  return (
    <WorkspaceModeProvider user={user} onRefreshUser={refreshUser}>
      <SatDownloadJobsProvider>
        <LayoutInner user={user} logout={logout} />
      </SatDownloadJobsProvider>
    </WorkspaceModeProvider>
  );
}

function LayoutInner({ user, logout }) {
  const location = useLocation();
  useWorkspacePresenceHeartbeat(Boolean(user));
  const navigate = useNavigate();
  const { mode, canSwitch } = useWorkspaceMode();
  const [activeWorkModule, setActiveWorkModule] = useState(getStoredWorkModule);
  const [clientPreviewActive, setClientPreviewActive] = useState(false);
  const isPlatformDesigner = usePlatformDesigner();
  const hasPlatformAccess = hasPlatformCenterAccess(user);
  const canSwitchAccessContext = hasMultipleAccessContexts(user);
  const { buyerPreviewActive, disableBuyerPreview } = usePlatformDesignerBuyerPreviewActions();
  const distributorEmisor = Boolean(
    !isPlatformDesigner
    && canSwitch
    && mode === "distribuidor"
    && user?.can_generate_licenses,
  );
  const showModulePanel = !user?.needs_trial_registration && (
    isPlatformDesigner || mode === WORKSPACE_MODE_PLATFORM || mode === "despacho" || distributorEmisor
  );
  const entitlementState = useEntitlements();
  const { entitlements, loading: entitlementsLoading } = entitlementState;
  const [reviewOwnerId, setReviewOwnerId] = useState(getDistributorReviewOwnerId);
  const [reviewBuyerId, setReviewBuyerId] = useState(getDistributorReviewBuyerId);
  const [reviewLabel, setReviewLabel] = useState(getDistributorReviewBuyerLabel);
  const [reviewBuyers, setReviewBuyers] = useState([]);
  const reviewActive = !isPlatformDesigner && Boolean(reviewOwnerId);
  const useModuleNav = showModulePanel && (isPlatformDesigner || !reviewActive);
  const isPanelHub = location.pathname === "/" || location.pathname === "";
  const { buyerModuleSidebarEnabled, appBackgroundTheme, appBackgroundCustom } = useUiPreferences();
  const { modules: buyerModuleVisibility } = useBuyerModuleVisibility();
  const routeEntitlementAccessCtx = useMemo(
    () => ({
      isPlatformDesigner,
      buyerPreviewActive,
      permLoading: entitlementsLoading,
      activeWorkModule,
      routeSearch: location.search,
      modulo_visor_sat: entitlementState.modulo_visor_sat,
      modulo_asistente_laboral: entitlementState.modulo_asistente_laboral,
      modulo_tramites_fiscales: entitlementState.modulo_tramites_fiscales,
      modulo_gestion_despacho: entitlementState.modulo_gestion_despacho,
      entitlements: entitlementState.entitlements,
      buyerModuleVisibility,
    }),
    [isPlatformDesigner, buyerPreviewActive, entitlementsLoading, activeWorkModule, location.search, entitlementState, buyerModuleVisibility],
  );
  const routeEntitlement = useMemo(
    () => requireModuleEntitlementForRoute(location.pathname, routeEntitlementAccessCtx),
    [location.pathname, routeEntitlementAccessCtx],
  );
  const showRouteEntitlementBlocked =
    useModuleNav && routeEntitlement.reason === "no_entitlement";
  const showRouteEntitlementLoading =
    useModuleNav && routeEntitlement.reason === "loading";
  const datosMaestrosNavActive = isDatosMaestrosNavigationActive(location);
  const showBuyerOperationalSidebar = resolveBuyerOperationalSidebar({
    buyerModuleSidebarEnabled,
    useModuleNav,
    isPlatformDesigner,
    activeWorkModule,
    isPanelHub,
  });
  const showDesignerModuleSidebar =
    isPlatformDesigner && activeWorkModule === WORK_MODULES.DISENADOR && useModuleNav;
  const showLegacySidebar = !isPanelHub && !useModuleNav && !isPlatformDesigner;
  const showMainSidebar =
    showBuyerOperationalSidebar
    || (!isPanelHub && (showDesignerModuleSidebar || showLegacySidebar));
  const navForRender = useMemo(
    () => {
      const base = resolveSidebarNav({
        canSwitch,
        mode,
        reviewActive,
        user,
        workModule: activeWorkModule,
        useModuleNav,
        pathname: location.pathname,
        isPlatformDesigner,
        buyerModuleSidebarEnabled,
      });
      const withPlatformLink = isPlatformDesigner
        ? [
            {
              to: "/platform",
              label: "Centro de Licencias BNT",
              icon: KeyRound,
              end: true,
              testid: "nav-centro-licencias-bnt",
            },
            ...base,
          ]
        : base;
      const filtered = filterNavByEntitlements(withPlatformLink, {
        isPlatformDesigner,
        entitlementsLoading,
        entitlementsState: entitlementState,
        activeWorkModule,
      });
      const withoutHonorarios = filterNavWithoutDespachoHonorarios(filtered, user);
      if (isPlatformDesigner) return withoutHonorarios;
      return withoutHonorarios.filter((item) => !item.designerOnly);
    },
    [
      canSwitch,
      mode,
      reviewActive,
      user,
      activeWorkModule,
      useModuleNav,
      location.pathname,
      entitlementsLoading,
      entitlementState,
      isPlatformDesigner,
      buyerModuleSidebarEnabled,
    ],
  );

  const handleWorkModuleChange = useCallback((moduleId) => {
    setActiveWorkModule(moduleId);
    if (distributorEmisor) {
      setClientPreviewActive(
        moduleId === WORK_MODULES.VISOR_SAT
        || moduleId === WORK_MODULES.ASISTENTE_LABORAL
        || moduleId === WORK_MODULES.TRAMITES_FISCALES,
      );
    }
  }, [distributorEmisor]);

  const clientScope = useMemo(
    () => resolveClientScope(location.pathname, activeWorkModule),
    [location.pathname, activeWorkModule],
  );
  const showModuleClientBar = !isHubOrAdminPath(location.pathname);

  const moduleInicioPath = useMemo(() => {
    if (useModuleNav) return getModuleInicioPath(activeWorkModule);
    if (isPlatformDesigner) return getModuleInicioPath(activeWorkModule);
    if (reviewActive) return getModuleInicioPath(WORK_MODULES.VISOR_SAT);
    if (canSwitch && mode === "distribuidor") return getModuleInicioPath(WORK_MODULES.DISENADOR);
    return getModuleInicioPath(activeWorkModule);
  }, [useModuleNav, activeWorkModule, reviewActive, canSwitch, mode, isPlatformDesigner]);

  const pathBase = location.pathname.split("?")[0];
  const isEquipoCommonPath = isEquipoBasePath(pathBase);
  const datosMaestrosReturnTo = datosMaestrosNavActive
    ? resolveDatosMaestrosReturnTarget(location)
    : null;
  const toolbarModuleBackPath = isEquipoCommonPath
    ? "/"
    : datosMaestrosReturnTo || moduleInicioPath;
  const toolbarModuleBackLabel = datosMaestrosNavActive
    ? "Volver a Datos Maestros"
    : "Inicio del módulo";
  const isOnModuleInicio = !isPanelHub && !isEquipoCommonPath && pathBase === moduleInicioPath;
  // Aviso de licencia solo en Panel General (PanelHubHeaderMeta). Evita repetir el bloque
  // ámbar al volver al inicio de cada módulo (/visor/inicio, /laboral/inicio, …).
  const showLicenseActivationOnHub = false;

  const showModuleNavToolbar = useModuleNav && !isPanelHub;

  const workModuleContextValue = useMemo(
    () => ({
      show: showModulePanel,
      activeModule: activeWorkModule,
      onModuleChange: handleWorkModuleChange,
      isPlatformDesigner,
    }),
    [showModulePanel, activeWorkModule, handleWorkModuleChange, isPlatformDesigner],
  );

  /** Sin platform_designer no se persiste el módulo Diseñador en localStorage. */
  useEffect(() => {
    if (!user) return;
    if (!isPlatformDesigner && activeWorkModule === WORK_MODULES.DISENADOR) {
      setStoredWorkModule(WORK_MODULES.VISOR_SAT);
      setActiveWorkModule(WORK_MODULES.VISOR_SAT);
    }
  }, [user, activeWorkModule, isPlatformDesigner]);

  useEffect(() => {
    if (mode !== "distribuidor") {
      setClientPreviewActive(false);
    }
  }, [mode]);

  /** Al entrar en modo Distribuidor (emisor), activar módulo Diseñador si el correo está autorizado. */
  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (isPlatformDesigner) return;
    if (
      prevModeRef.current !== mode &&
      mode === "distribuidor" &&
      distributorEmisor &&
      !reviewActive
    ) {
      setStoredWorkModule(WORK_MODULES.DISENADOR);
      setActiveWorkModule(WORK_MODULES.DISENADOR);
      setClientPreviewActive(false);
      navigate(getModuleInicioPath(WORK_MODULES.DISENADOR), { replace: true });
    }
    prevModeRef.current = mode;
  }, [mode, distributorEmisor, reviewActive, navigate, isPlatformDesigner]);

  useEffect(() => {
    const onModuleChanged = (e) => {
      setActiveWorkModule(e?.detail?.moduleId || getStoredWorkModule());
    };
    window.addEventListener("visor:work-module-changed", onModuleChanged);
    return () => window.removeEventListener("visor:work-module-changed", onModuleChanged);
  }, []);

  useEffect(() => {
    document.title = VISOR_MODULE_BROWSER_TITLE;
  }, [useModuleNav, activeWorkModule]);

  useEffect(() => {
    if (!useModuleNav) return;
    const path = location.pathname.split("?")[0];
    if (!isDatosMaestrosHubCardPath(path)) return;
    const freshContext =
      location.state?.navigationContext === DATOS_MAESTROS_NAV_CONTEXT
      || Boolean(parseDatosMaestrosReturnFromSearch(location.search));
    if (freshContext) {
      captureDatosMaestrosReturnContext(location);
    } else if (!readDatosMaestrosReturnFromSession()) {
      clearDatosMaestrosReturnContext();
    }
  }, [useModuleNav, location.pathname, location.search, location.state]);

  useEffect(() => {
    if (!useModuleNav) return;
    const path = location.pathname.split("?")[0];
    if (isHubOrAdminPath(path) || isEquipoBasePath(path)) return;
    if (isAnalisisFinancieroPath(path) && activeWorkModule !== WORK_MODULES.ANALISIS_FINANCIERO) {
      setStoredWorkModule(WORK_MODULES.ANALISIS_FINANCIERO);
      setActiveWorkModule(WORK_MODULES.ANALISIS_FINANCIERO);
    }
    const isMaterialidadRoute =
      MATERIALIDAD_MODULE_PATHS.has(path) || path.startsWith("/materialidad/");
    if (isMaterialidadRoute && activeWorkModule !== WORK_MODULES.MATERIALIDAD) {
      setStoredWorkModule(WORK_MODULES.MATERIALIDAD);
      setActiveWorkModule(WORK_MODULES.MATERIALIDAD);
    }
    const isTramitesRoute = TRAMITES_MODULE_PATHS.has(path) || TRAMITES_LEGACY_PATHS.has(path);
    if (
      isTramitesRoute
      && !isMaterialidadRoute
      && path !== "/tramites/materialidad"
      && activeWorkModule !== WORK_MODULES.TRAMITES_FISCALES
    ) {
      setStoredWorkModule(WORK_MODULES.TRAMITES_FISCALES);
      setActiveWorkModule(WORK_MODULES.TRAMITES_FISCALES);
    }
    const isDespachoRoute = DESPACHO_MODULE_PATHS.has(path) || path.startsWith("/despacho/");
    if (isDespachoRoute && activeWorkModule !== WORK_MODULES.GESTION_DESPACHO) {
      setStoredWorkModule(WORK_MODULES.GESTION_DESPACHO);
      setActiveWorkModule(WORK_MODULES.GESTION_DESPACHO);
    }
    if (shouldBypassWorkModulePathGuard(location)) {
      const dmModule = resolveWorkModuleForDatosMaestrosCard(path);
      if (dmModule && activeWorkModule !== dmModule) {
        setStoredWorkModule(dmModule);
        setActiveWorkModule(dmModule);
      }
    }
  }, [useModuleNav, location.pathname, location.search, location.state, activeWorkModule]);

  useEffect(() => {
    if (!useModuleNav) return;
    const path = location.pathname.split("?")[0];
    const exempt = ["/registro-inicial", "/recuperacion-acceso", "/datos-maestros", "/gestion-datos", "/exportacion-local", "/gestionar-xml"];
    if (exempt.includes(path)) return;
    if (isEquipoBasePath(path)) return;
    if (LABORAL_LEGACY_REDIRECT_PATHS.has(path)) return;
    if (isHubOrAdminPath(path)) return;
    if (isPanelHub) return;
    if (isPlatformDesigner && (path === "/platform" || path.startsWith("/platform/"))) return;
    if (shouldBypassWorkModulePathGuard(location)) return;
    const effectiveModule = getStoredWorkModule();
    if (effectiveModule !== activeWorkModule) {
      setActiveWorkModule(effectiveModule);
    }
    if (!isPathAllowedForModule(path, effectiveModule, location.search, location.state)) {
      navigate(getModuleInicioPath(effectiveModule), { replace: true });
    }
  }, [useModuleNav, location.pathname, location.search, location.state, activeWorkModule, navigate, isPanelHub, isPlatformDesigner]);

  useEffect(() => {
    const syncReview = () => {
      setReviewOwnerId(getDistributorReviewOwnerId());
      setReviewBuyerId(getDistributorReviewBuyerId());
      setReviewLabel(getDistributorReviewBuyerLabel());
      setStoredClientId("");
    };
    window.addEventListener("visor:distributor-review-changed", syncReview);
    return () => window.removeEventListener("visor:distributor-review-changed", syncReview);
  }, []);

  useEffect(() => {
    if (mode === "despacho") clearDistributorReview();
  }, [mode]);

  useEffect(() => {
    if (isPlatformDesigner || !canSwitch || mode !== "distribuidor" || !user?.can_generate_licenses) {
      setReviewBuyers([]);
      return;
    }
    api
      .get("/distributor/buyers")
      .then(({ data }) => setReviewBuyers(data?.items || []))
      .catch(() => setReviewBuyers([]));
  }, [isPlatformDesigner, canSwitch, mode, user?.can_generate_licenses]);

  useEffect(() => {
    if (!hasPlatformAccess) return;
    if (isBuyerLicenseConfigDeepLink(location.pathname, location.search)) {
      navigate("/platform", { replace: true });
    }
  }, [hasPlatformAccess, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (user?.needs_trial_registration && location.pathname !== "/registro-inicial") {
      navigate("/registro-inicial", { replace: true });
    }
  }, [user?.needs_trial_registration, location.pathname, navigate]);

  useEffect(() => {
    if (user?.password_recovery_only && location.pathname !== "/recuperacion-acceso") {
      navigate("/recuperacion-acceso", { replace: true });
    }
  }, [user?.password_recovery_only, location.pathname, navigate]);

  useEffect(() => {
    if (user && !user.needs_trial_registration && location.pathname === "/registro-inicial") {
      navigate("/", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    if (isPlatformDesigner || mode === WORKSPACE_MODE_PLATFORM) return;
    if (!canSwitch || mode !== "distribuidor") return;
    if (isDistribuidorAllowedPath(location.pathname, reviewActive, activeWorkModule, useModuleNav)) return;
    toast.info(
      reviewActive
        ? "Ruta no disponible en revisión de comprador."
        : "Modo Distribuidor: elige un comprador abajo para revisar su cartera, o cambia a Despacho para tu operación.",
      { duration: 5500 },
    );
    navigate(defaultRouteForModule(activeWorkModule), { replace: true });
  }, [
    isPlatformDesigner,
    canSwitch,
    mode,
    location.pathname,
    navigate,
    reviewActive,
    activeWorkModule,
    useModuleNav,
  ]);
  const [ownerProfile, setOwnerProfile] = useState({
    logo_base64: "",
    nombre_comercial: "",
    razon_social: "",
    rfc: "",
  });
  const [uiServingHint, setUiServingHint] = useState(null);
  /** Mini-sidebar (contraída) por defecto para comprador y diseñador; solo se expande con el chevron. */
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  const sidebarExpanded = !sidebarCollapsed;

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  /** Al cambiar de ruta o módulo (incluido Panel General), volver al sidebar contraído. */
  useEffect(() => {
    setSidebarCollapsed(true);
  }, [location.pathname, activeWorkModule]);

  useEffect(() => {
    api
      .get("/system/info")
      .then(({ data }) => {
        const p = data?.profile || {};
        setOwnerProfile({
          logo_base64: p.logo_base64 || "",
          nombre_comercial: p.nombre_comercial || "",
          razon_social: p.razon_social || "",
          rfc: p.rfc || "",
        });
      })
      .catch(() => {
        setOwnerProfile({ logo_base64: "", nombre_comercial: "", razon_social: "", rfc: "" });
      });
  }, [mode, user?.id]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    const p = String(window.location.port || "").trim();
    // CRA / Vite habituales: no consultar hint (ya es UI viva con yarn start).
    if (["3000", "3001", "3002", "5173", "5174"].includes(p)) {
      setUiServingHint(null);
      return;
    }
    let cancelled = false;
    api
      .get("/system/ui-serving-hint")
      .then(({ data }) => {
        if (!cancelled) setUiServingHint(data);
      })
      .catch(() => {
        if (!cancelled) setUiServingHint(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Aviso solo si esta pestaña es puerto típico API+SPA empaquetada (no yarn start). */
  const showDevStaticSpaWarning =
    process.env.NODE_ENV === "development" &&
    Boolean(uiServingHint?.spa_static_active) &&
    ["8000", "8001", "8080"].includes(String(window.location.port || "").trim());

  if (user?.needs_trial_registration && location.pathname === "/registro-inicial") {
    return <Outlet />;
  }

  return (
    <div className="relative min-h-screen flex flex-col bg-[#F4F6F8]">
      <AppBackgroundLayer theme={appBackgroundTheme} custom={appBackgroundCustom} />
      <div className="relative z-10 flex flex-1 min-h-0">
      {showMainSidebar ? (
      <>
      {sidebarCollapsed ? <div className={SIDEBAR_MINI_WIDTH_CLASS} aria-hidden /> : null}
      <aside
        className={`${
          sidebarExpanded ? "w-60 shrink-0" : SIDEBAR_MINI_WIDTH_CLASS
        } bg-[#0F2D5C] text-white flex flex-col min-h-screen transition-[width] duration-300 ease-in-out ${
          sidebarCollapsed
            ? "fixed left-0 top-0 bottom-0 z-40"
            : "relative"
        }`}
        data-testid="main-sidebar"
        data-sidebar-collapsed={sidebarCollapsed ? "true" : "false"}
      >
        <div className={`${sidebarExpanded ? "px-5 py-5" : "px-2 py-3"} border-b border-white/10 flex items-start gap-2 shrink-0`}>
          <div className={`${sidebarExpanded ? "min-w-0 flex-1" : "w-full flex justify-center shrink-0"}`}>
            {sidebarExpanded ? (
              <>
                <BNTLogo
                  size="sm"
                  inverted
                  customLogo={(ownerProfile.logo_base64 || "").trim()}
                  customName={
                    (ownerProfile.nombre_comercial || ownerProfile.razon_social || "").trim()
                  }
                  customSubtitle={(ownerProfile.rfc || "").trim()}
                />
                {!(ownerProfile.nombre_comercial || ownerProfile.razon_social) && (
                  <div className="text-[9px] uppercase tracking-[0.18em] text-white/50 mt-2">
                    {VISOR_MODULE_DISPLAY_NAME}
                  </div>
                )}
              </>
            ) : (
              <div
                className="w-9 h-9 rounded-sm bg-white/10 flex items-center justify-center text-[10px] font-bold tracking-tight"
                title={VISOR_MODULE_DISPLAY_NAME}
                aria-hidden
              >
                <span className="text-white">B</span>
                <span className="text-[#3DA873]">N</span>
                <span className="text-white">T</span>
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggleSidebarCollapsed}
            className={`shrink-0 text-white/70 hover:bg-white/10 hover:text-white rounded-sm h-8 w-8 ${
              !sidebarExpanded ? "hidden" : ""
            }`}
            title={sidebarCollapsed ? "Expandir menú lateral" : "Contraer menú lateral"}
            aria-expanded={sidebarExpanded}
            data-testid="sidebar-collapse-toggle"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </Button>
        </div>
        {!sidebarExpanded ? (
          <div className="px-2 py-1 border-b border-white/10 flex justify-center">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleSidebarCollapsed}
              className="text-white/70 hover:bg-white/10 hover:text-white rounded-sm h-8 w-8"
              title="Expandir menú lateral"
              data-testid="sidebar-collapse-toggle-collapsed"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        ) : null}
        <TooltipProvider delayDuration={250}>
        <nav
          className={`flex-1 ${sidebarExpanded ? "p-3 overflow-x-hidden" : "p-1.5 overflow-x-visible"} space-y-0.5 overflow-y-auto`}
          data-nav-revision="2026-05-26-mini-sidebar-icons"
        >
          {navForRender.map((item, idx) => {
            const Icon = item.icon || MODULE_NAV_ICON_MAP[item.iconKey] || LayoutDashboard;
            const tid =
              item.testid ||
              `nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`;
            const prevSection = idx > 0 ? navForRender[idx - 1]?.navSection : null;
            const showSection = sidebarExpanded && item.navSection && item.navSection !== prevSection;
            const navLinkClassExpanded = ({ isActive }) =>
              `flex items-center w-full gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors ${
                isActive
                  ? "bg-white text-[#0F2D5C] font-semibold border-l-2 border-[#3DA873]"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`;
            const navLinkClassMini = ({ isActive }) =>
              `vf-sidebar-mini-link flex items-center justify-center w-full h-10 min-h-10 shrink-0 rounded-sm text-sm transition-colors ${
                isActive
                  ? "bg-white text-[#0F2D5C] font-semibold"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`;
            const navIconMini = (
              <span className="vf-sidebar-icon-slot inline-flex items-center justify-center w-5 h-5 shrink-0 flex-none pointer-events-none">
                <Icon className="block w-4 h-4 min-w-[1rem] min-h-[1rem]" strokeWidth={1.8} aria-hidden />
              </span>
            );
            const navLinkActiveProps = item.tramitesTabId
              ? { isActive: (_match, loc) => isTramitesTabNavActive(loc, item.tramitesTabId) }
              : {};
            return (
              <div key={`${item.to}::${item.label}`} className={sidebarExpanded ? undefined : "w-full shrink-0"}>
                {showSection ? (
                  <div
                    className="px-3 pt-3 pb-1 text-[9px] uppercase tracking-[0.12em] text-white/45 font-semibold first:pt-0"
                    data-testid={`nav-section-${item.navSection.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {item.navSection}
                  </div>
                ) : null}
                {!sidebarExpanded ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-full shrink-0">
                        <NavLink
                          to={item.to}
                          end={item.end}
                          {...navLinkActiveProps}
                          data-testid={tid}
                          className={navLinkClassMini}
                          aria-label={item.label}
                        >
                          {navIconMini}
                        </NavLink>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      sideOffset={12}
                      className="z-[100] bg-[#0F2D5C] text-white border border-white/20 shadow-md"
                    >
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <NavLink
                    to={item.to}
                    end={item.end}
                    {...navLinkActiveProps}
                    data-testid={tid}
                    className={navLinkClassExpanded}
                  >
                    <Icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                    <span className="flex-1 leading-snug">{item.label}</span>
                    {item.navStep ? (
                      <span
                        className="text-[9px] font-bold tabular-nums rounded px-1 py-0.5 bg-white/10 text-white/55"
                        aria-label={`Paso ${item.navStep}`}
                      >
                        {item.navStep}
                      </span>
                    ) : null}
                  </NavLink>
                )}
              </div>
            );
          })}
        </nav>
        {showDevStaticSpaWarning && sidebarExpanded ? (
          <div
            className="mx-2 mb-2 rounded-sm border border-amber-400/50 bg-amber-500/15 px-2 py-2 text-[10px] leading-snug text-amber-100"
            data-testid="vf-dev-static-spa-warning"
          >
            <span className="font-semibold">Desarrollo:</span> esta pestaña usa el <strong>mismo puerto que la API</strong>{" "}
            (<span className="font-mono">:{window.location.port || "?"}</span>). El backend sirve la UI desde{" "}
            <code className="rounded bg-black/20 px-0.5">frontend_build</code> (sin hot-reload de{" "}
            <code className="rounded bg-black/20 px-0.5">yarn start</code>
            ). Para cambios de React en vivo, abre la app en el puerto del dev server (p. ej. :3000).
          </div>
        ) : null}
        <div className={`${sidebarExpanded ? "p-3" : "p-2"} border-t border-white/10 space-y-2`}>
          {isPlatformDesigner && sidebarExpanded ? (
            <div
              className="mx-2 mb-2 rounded-sm border border-sky-400/40 bg-sky-500/15 px-2 py-2 text-[10px] leading-snug text-sky-100"
              data-testid="platform-designer-mode-banner"
            >
              <span className="font-semibold uppercase tracking-wider">Modo Plataforma BNT</span>
              <span className="block text-white/70 mt-0.5">Superusuario — acceso global sin cartera comercial</span>
            </div>
          ) : null}
          {!isPlatformDesigner && canSwitch && mode === "distribuidor" && user?.can_generate_licenses && sidebarExpanded ? (
            <div className="px-3 pb-2" data-testid="distributor-review-buyer-select">
              <div className="text-[10px] text-white/50 uppercase tracking-wider mb-1">
                Revisar comprador
              </div>
              <Select
                value={reviewBuyerId || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") {
                    clearDistributorReview();
                    return;
                  }
                  const b = reviewBuyers.find((x) => x.id === v);
                  if (!b) return;
                  if (!b.reviewable || !b.review_owner_user_id) {
                    toast.info(
                      "Este comprador no tiene espacio operativo en este servidor (falta usuario portal o empresas).",
                      { duration: 5000 },
                    );
                    return;
                  }
                  setDistributorReview({
                    buyerId: b.id,
                    ownerUserId: b.review_owner_user_id,
                    label: b.licensee_name || b.licensee_rfc,
                  });
                  toast.success(`Revisando: ${b.licensee_name || b.licensee_rfc}`);
                }}
              >
                <SelectTrigger className="h-9 bg-white/10 border-white/25 text-white text-xs rounded-sm [&_svg]:text-white/80">
                  <SelectValue placeholder="Seleccionar comprador" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin revisión —</SelectItem>
                  {reviewBuyers.map((b) => (
                    <SelectItem key={b.id} value={b.id} disabled={!b.reviewable}>
                      {b.licensee_name || b.licensee_rfc}
                      {b.reviewable
                        ? ` · ${b.local_clients_count ?? 0} empresa(s)`
                        : " · sin espacio local"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {sidebarExpanded ? (
          <div className="px-3 py-2">
            <div className="text-xs text-white/60">Sesión</div>
            <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-[10px] text-white/50 truncate font-mono">{user?.email}</div>
          </div>
          ) : null}
          {canSwitchAccessContext ? (
            sidebarExpanded ? (
              <Button
                type="button"
                data-testid="switch-access-context-btn"
                variant="ghost"
                className="w-full justify-start text-white/70 hover:bg-white/10 hover:text-white rounded-sm"
                onClick={() => navigate("/seleccionar-contexto?cambiar=1")}
              >
                <UserCog className="w-4 h-4 mr-2 block shrink-0" />
                Cambiar contexto
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    data-testid="switch-access-context-btn"
                    variant="ghost"
                    size="icon"
                    className="vf-sidebar-mini-link w-full h-10 min-h-10 justify-center text-white/70 hover:bg-white/10 hover:text-white rounded-sm"
                    aria-label="Cambiar contexto"
                    onClick={() => navigate("/seleccionar-contexto?cambiar=1")}
                  >
                    <UserCog className="block w-4 h-4 min-w-[1rem] min-h-[1rem] shrink-0" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  sideOffset={12}
                  className="z-[100] bg-[#0F2D5C] text-white border border-white/20 shadow-md"
                >
                  Cambiar contexto
                </TooltipContent>
              </Tooltip>
            )
          ) : null}
          {sidebarExpanded ? (
          <Button
            data-testid="logout-btn"
            onClick={async () => {
              if (!window.confirm("¿Cerrar sesión?")) return;
              await logout();
            }}
            variant="ghost"
            className="w-full justify-start text-white/70 hover:bg-white/10 hover:text-white rounded-sm"
            title="Cierra la sesión actual."
          >
            <LogOut className="w-4 h-4 mr-2 block shrink-0" />
            Cerrar sesión
          </Button>
          ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                data-testid="logout-btn"
                onClick={async () => {
                  if (!window.confirm("¿Cerrar sesión?")) return;
                  await logout();
                }}
                variant="ghost"
                size="icon"
                className="vf-sidebar-mini-link w-full h-10 min-h-10 justify-center text-white/70 hover:bg-white/10 hover:text-white rounded-sm"
                aria-label="Cerrar sesión"
              >
                <LogOut className="block w-4 h-4 min-w-[1rem] min-h-[1rem] shrink-0" />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={12}
              className="z-[100] bg-[#0F2D5C] text-white border border-white/20 shadow-md"
            >
              Cerrar sesión
            </TooltipContent>
          </Tooltip>
          )}
          {sidebarExpanded ? (
          <div className="mt-4 px-3 py-1 border-t border-white/5">
            <div className="text-[10px] text-white/30 font-bold tracking-widest uppercase text-center">
              {VISOR_MODULE_DISPLAY_NAME}
            </div>
            <div
              className="text-[9px] text-white/45 font-mono text-center mt-1.5 tracking-tight"
              data-testid="vf-sidebar-revision"
              title="Si este ID no coincide con el último despliegue, recarga dura o revisa si entras por :8000 con frontend_build."
            >
              UI {VF_UI_BUILD_ID}
            </div>
          </div>
          ) : null}
        </div>
        </TooltipProvider>
      </aside>
      </>
      ) : null}
      <main className="flex-1 overflow-x-auto flex flex-col min-h-0">
        {isPanelHub ? (
          <>
            <PanelHubTopbar
              ownerProfile={ownerProfile}
              user={user}
              onLogout={async () => {
                if (!window.confirm("¿Cerrar sesión?")) return;
                await logout();
              }}
            />
            <PanelHubHeaderMeta
              showClientSelector={isPlatformDesigner || !distributorEmisor || clientPreviewActive}
            />
          </>
        ) : null}
        {!isPlatformDesigner && reviewActive && reviewLabel ? (
          <div className="bg-violet-950 text-violet-50 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 border-b border-violet-800 shrink-0" data-testid="distributor-review-banner">
            <p className="text-xs">
              <span className="font-semibold">Revisando comprador:</span> {reviewLabel}
              <span className="text-violet-200/80"> — solo consulta (cartera en este servidor)</span>
            </p>
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-violet-400 text-violet-50 hover:bg-violet-900" onClick={() => clearDistributorReview()}>
              Salir de revisión
            </Button>
          </div>
        ) : null}
        {!isPlatformDesigner && distributorEmisor && clientPreviewActive ? (
          <div
            className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex flex-wrap items-center justify-between gap-2 shrink-0"
            data-testid="distributor-client-preview-banner"
          >
            <p className="text-xs text-amber-950">
              <span className="font-semibold">Vista previa del cliente</span>
              <span className="text-amber-800/90"> — menú y módulos como los verá el comprador en modo Despacho.</span>
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs border-amber-400 text-amber-950 hover:bg-amber-100"
              onClick={() => {
                setClientPreviewActive(false);
                navigate("/");
              }}
            >
              Volver a vista distribuidor
            </Button>
          </div>
        ) : null}
        {isPlatformDesigner && buyerPreviewActive ? (
          <div
            className="bg-sky-50 border-b border-sky-200 px-4 py-2 flex flex-wrap items-center justify-between gap-2 shrink-0"
            data-testid="platform-designer-buyer-preview-banner"
          >
            <p className="text-xs text-sky-950">
              <span className="font-semibold">Vista de prueba como comprador</span>
              <span className="text-sky-800/90"> — {PLATFORM_DESIGNER_BUYER_PREVIEW_BANNER}</span>
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs border-sky-400 text-sky-950 hover:bg-sky-100"
              onClick={() => {
                disableBuyerPreview();
                navigate("/");
              }}
              data-testid="platform-designer-buyer-preview-exit"
            >
              Vista diseñador
            </Button>
          </div>
        ) : null}
        <PlatformLicenseRequestsPendingBanner />
        <ClientModuleScopeProvider scope={clientScope}>
          {showModuleClientBar ? (
            <ModuleClientSelector
              key={`module-client-bar:${clientScope}:${location.pathname}`}
              variant="bar"
            />
          ) : null}
          {showModuleNavToolbar ? (
            <div
              className="shrink-0 border-b border-slate-200 bg-white px-4 py-2 sm:px-6 flex flex-wrap items-center gap-2"
              data-testid="module-toolbar"
            >
              {isEquipoCommonPath ? (
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
                  title="Volver al Panel General"
                  data-testid="back-to-panel-general"
                >
                  <ArrowLeft className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                  Panel General
                </Link>
              ) : (
                <>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 rounded-sm border border-[#0F2D5C]/25 bg-[#0F2D5C]/[0.04] px-3 py-1.5 text-xs font-semibold text-[#0F2D5C] shadow-sm hover:bg-[#0F2D5C]/10 hover:border-[#0F2D5C]/40 transition-colors"
                    title="Volver al Panel General"
                    data-testid="back-to-panel-general"
                  >
                    <LayoutDashboard className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                    Panel General
                  </Link>
                  {isOnModuleInicio ? (
                    activeWorkModule === WORK_MODULES.ASISTENTE_LABORAL ? (
                      <button
                        type="button"
                        onClick={() => requestLaboralFlujoCollapseToHub()}
                        className="inline-flex items-center gap-2 rounded-sm border border-[#3DA873]/40 bg-[#3DA873]/10 px-3 py-1.5 text-xs font-semibold text-[#0F2D5C] shadow-sm hover:bg-[#3DA873]/15 transition-colors"
                        title="Ver todas las tarjetas del flujo operativo"
                        aria-current="page"
                        data-testid="module-inicio-active"
                      >
                        <ArrowLeft className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                        Inicio del módulo
                      </button>
                    ) : (
                      <span
                        className="inline-flex items-center gap-2 rounded-sm border border-[#3DA873]/40 bg-[#3DA873]/10 px-3 py-1.5 text-xs font-semibold text-[#0F2D5C] shadow-sm"
                        title={`Está en el inicio del módulo (${moduleInicioPath})`}
                        aria-current="page"
                        data-testid="module-inicio-active"
                      >
                        <ArrowLeft className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                        Inicio del módulo
                      </span>
                    )
                  ) : (
                    <Link
                      to={toolbarModuleBackPath}
                      className="inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
                      title={
                        datosMaestrosNavActive
                          ? "Volver a Datos Maestros"
                          : `Volver al inicio del módulo (${moduleInicioPath})`
                      }
                      data-testid={datosMaestrosNavActive ? "back-to-datos-maestros" : "back-to-module-inicio"}
                    >
                      <ArrowLeft className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                      {toolbarModuleBackLabel}
                    </Link>
                  )}
                </>
              )}
              <div className="ml-auto flex items-center gap-2">
                <SatDownloadJobsIndicator />
                <ModuleSidebarQuickToggle />
                {!showMainSidebar ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs rounded-sm border-[#0F2D5C]/20 text-[#0F2D5C] hover:bg-slate-50"
                    data-testid="module-toolbar-logout"
                    onClick={async () => {
                      if (!window.confirm("¿Cerrar sesión?")) return;
                      await logout();
                    }}
                  >
                    <LogOut className="w-4 h-4 mr-1.5 shrink-0" />
                    Cerrar sesión
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
          {showLicenseActivationOnHub ? (
            <div
              className="shrink-0 border-b border-slate-100 bg-white px-4 py-3 sm:px-6"
              data-testid="module-hub-license-activation-strip"
            >
              <PanelLicenseActivationBlock />
            </div>
          ) : null}
          <SatDownloadCompletionBanner />
          <CertificateExpirationBanner />
          <div className="flex-1 min-h-0 overflow-x-auto">
            <WorkModuleProvider value={workModuleContextValue}>
              {showRouteEntitlementBlocked ? (
                <RouteModuleEntitlementBlocked />
              ) : showRouteEntitlementLoading ? (
                <div
                  className="flex min-h-[40vh] items-center justify-center p-8 text-sm text-slate-500"
                  data-testid="route-entitlement-loading"
                >
                  Verificando licencia del módulo…
                </div>
              ) : (
                <Outlet key={`${mode}-${reviewOwnerId || "none"}`} />
              )}
            </WorkModuleProvider>
          </div>
        </ClientModuleScopeProvider>
      </main>
      </div>
      <ClientRepositoryRelocateDialog enabled={false} />
      {user && !isBntAsistentePath(location.pathname) && !user?.needs_trial_registration ? (
        <BntAsistenteShell />
      ) : null}
    </div>
  );
}

